import { Stack } from '@mui/material'
import { useThreadOverviewPage } from 'ClientPortal/src/pages/ThreadOverview/ThreadOverviewPage.data'
import { useFormQueryParams } from 'ClientPortal/src/router/useFormQueryParams'
import {
    CreateNewThreadDialog,
    Footer,
    MessageListEmptyState,
    OrderDropdown,
    ThreadSelection,
    useHideScrollbarWhenOtherFocused,
} from 'ComponentLibrary'
import { isEmpty } from 'lodash'
import { useThreadSelection } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/useThreadSelection'
import { ThreadsMessageList } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/ThreadsMessageList'
import { useDeleteThread } from 'ClientPortal/src/api/Queries/threads'
import { useUrlState } from 'ComponentLibrary/src/hooks/useUrlState'
import { ThreadOverviewPageUrlStateSchema } from 'ClientPortal/src/pages/schemas.zod'
import { useTranslation } from 'react-i18next'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export function ThreadOverviewPage() {
    const { t } = useTranslation('pages', { keyPrefix: 'threadOverviewPage' })
    const locale = useCurrentResolvedLocale()
    const { urlState, setUrlState } = useUrlState(ThreadOverviewPageUrlStateSchema, 'client-thread-filter')

    const {
        threads,
        isThreadsPending,
        isOrderOverviewsPending,
        actualOrderOverviews,
        users,
        isCreatingThread,
        onCreateThreadSubmit,
        pinThread,
        unpinThread,
    } = useThreadOverviewPage({
        orderFilterValue: urlState.orderFilterValue || undefined,
    })

    const { createMode, setCreateMode } = useFormQueryParams()

    const { selectedThread, onClickThread } = useThreadSelection({ threads })

    const hasThreads = !isEmpty(threads)

    const { scrollableContainerRef, resetScrollbar, hideScrollbar } = useHideScrollbarWhenOtherFocused()

    const defaultSelectedOrder = actualOrderOverviews.find((order) => order.uuid === urlState.orderFilterValue)
    const userLinkCopy = useUserLinkCopy()
    const threadCopy = {
        noMessages: t('thread.noMessages'),
        deletedMessage: t('thread.deletedMessage'),
        createdBy: (userName: string) => t('thread.createdBy', { userName }),
        createdAutomatically: t('thread.createdAutomatically'),
    }
    const selectionCopy = {
        newThread: t('selection.newConversation'),
        item: {
            noTitle: t('selection.item.noTitle'),
            notifiedUsersTooltip: t('selection.item.notifiedUsersTooltip'),
            options: t('selection.item.options'),
            unpin: t('selection.item.unpin'),
            pin: t('selection.item.pin'),
            delete: t('selection.item.delete'),
            thread: threadCopy,
        },
    }
    const createDialogCopy = {
        title: t('createDialog.title'),
        create: t('createDialog.create'),
        cancel: t('createDialog.cancel'),
        topicLabel: t('createDialog.topicLabel'),
        participantsLabel: t('createDialog.participantsLabel'),
        defaultNotifiedUsers: t('createDialog.defaultNotifiedUsers'),
        yourself: t('createDialog.yourself'),
        creator: t('createDialog.creator'),
        observers: t('createDialog.observers'),
        assignees: t('createDialog.assignees'),
        observersDescriptionSuffix: t('createDialog.observersDescriptionSuffix'),
        orderDropdown: {
            loading: t('orderDropdown.loading'),
            label: t('orderDropdown.label'),
            noOptions: t('orderDropdown.noOptions'),
            valuationDate: t('orderDropdown.valuationDate'),
            orderDate: t('orderDropdown.orderDate'),
        },
    }
    const emptyStateCopy = {
        loadingTitle: t('emptyState.loadingTitle'),
        emptyTitle: t('emptyState.emptyTitle'),
        emptyDescriptionLineOne: t('emptyState.emptyDescriptionLineOne'),
        emptyDescriptionLineTwo: t('emptyState.emptyDescriptionLineTwo'),
        newThread: t('emptyState.newConversation'),
    }
    const orderDropdownCopy = {
        loading: t('orderDropdown.loading'),
        label: t('orderDropdown.label'),
        noOptions: t('orderDropdown.noOptions'),
        valuationDate: t('orderDropdown.valuationDate'),
        orderDate: t('orderDropdown.orderDate'),
    }

    return (
        <Stack direction="row">
            <ThreadSelection
                title={t('title')}
                threadListRef={scrollableContainerRef}
                threads={threads}
                activeThreadId={selectedThread?.uuid}
                onClickThread={onClickThread}
                createMode={createMode}
                setCreateMode={setCreateMode}
                footer={<Footer />}
                isLoadingThreads={isThreadsPending}
                isLoadingOrderData={isOrderOverviewsPending}
                pinThread={pinThread}
                unpinThread={unpinThread}
                useDeleteThread={useDeleteThread}
                copy={selectionCopy}
                userLinkCopy={userLinkCopy}
                locale={locale}
                filterConfig={{
                    filters: [
                        <OrderDropdown
                            key="OrderDropdown"
                            orders={actualOrderOverviews}
                            filterValue={urlState.orderFilterValue}
                            setFilterValue={(orderFilterValue) => setUrlState((prev) => ({ ...prev, orderFilterValue }))}
                            onOpen={hideScrollbar}
                            onClose={resetScrollbar}
                            isLoadingOrderData={isOrderOverviewsPending}
                            copy={orderDropdownCopy}
                            locale={locale}
                        />,
                    ],
                    noResultsText: t('filters.noResults'),
                    isAnyFilterActive: urlState.orderFilterValue !== null,
                }}
            />
            <CreateNewThreadDialog
                open={createMode}
                onClose={() => setCreateMode(false)}
                orderOptions={actualOrderOverviews}
                isCreatingThread={isCreatingThread}
                onSubmit={onCreateThreadSubmit}
                participantsProps={{ users }}
                key={urlState.orderFilterValue}
                defaultValues={defaultSelectedOrder ? { selectedOrder: defaultSelectedOrder } : undefined}
                copy={createDialogCopy}
                userLinkCopy={userLinkCopy}
                locale={locale}
            />
            {hasThreads && !isThreadsPending && selectedThread ? (
                <ThreadsMessageList thread={selectedThread} />
            ) : (
                <MessageListEmptyState isLoading={isThreadsPending} onNewThreadClick={() => setCreateMode(true)} copy={emptyStateCopy} />
            )}
        </Stack>
    )
}
